import { configHome, configTeaser, DataSource } from "@pub/config";
import placeholders from "@pub/placeholders";
import {
  Ads,
  Card,
  ContentBlock,
  LatestArticle,
  SectionContext,
  useCustomClassContext,
  useMediaQuery,
} from "@sphtech/dmg-design-system";
import { getVariantByIndex } from "@src/app/utils/VariantByIndex";
import cx from "classnames";
import React from "react";
import { ReactNode } from "react";

import styles from "../homelayout.module.css";
import { extractHomeContentData, HomeLayoutProps } from "./utils";

type Props = {
  data: HomeLayoutProps["data"];
  dataSource: DataSource;
  alternateBg?: boolean;
  variant?: "primary" | "secondary";
};

/**
 * Description: Content Block G (4 tiles with IMU)
 */
export default function HomeContentBlockG({
  data,
  dataSource,
  alternateBg,
  variant = "primary",
}: Props): ReactNode {
  const customClass = useCustomClassContext();
  const contentBlockG = extractHomeContentData(data, dataSource);
  const mobile = useMediaQuery("screen and (max-width: 1000px)");
  const SidebarAdComponent = configHome.contentBlockG?.imuType
    ? Ads[configHome.contentBlockG.imuType]
    : Ads["AdsSideBar"];
  const cardConfig = dataSource.card || configHome.contentBlockG?.card;

  return (
    contentBlockG && (
      <div
        className={cx(
          alternateBg && styles.backgroundFill,
          styles.gutter,
          styles.contentBlockGContainer,
          customClass?.homeContentBlockG?.sectionHeading,
        )}
        style={
          contentBlockG.config && {
            background: contentBlockG.config.background,
          }
        }
      >
        <div className={cx(styles.bodyContainer)}>
          <ContentBlock
            heading={contentBlockG.title}
            headingZH={contentBlockG.titleZH}
            cta={{
              name: configHome.contentBlockF?.viewMore?.text || "VIEW ALL",
              url: contentBlockG.url,
              icon: configHome.viewMore?.icon,
            }}
            imu={
              !configHome.contentBlockG?.hideIMU &&
              !(mobile && configHome.contentBlockG?.hideIMUMobile) ? (
                <SidebarAdComponent />
              ) : null
            }
            withDivider={configHome.headingDivider}
            isHeadingClickable={configHome.isHeadingClickable}
          >
            <SectionContext.Provider
              value={{
                uniqueName: configHome.contentBlockG?.skipSectionContext
                  ? undefined
                  : contentBlockG.uniqSectionName,
              }}
            >
              <div className={cx(styles.latestTeaserFontOverride, styles.row)}>
                {contentBlockG.items.map((item, index) => {
                  return (
                    <React.Fragment key={index}>
                      <div
                        key={index}
                        className={cx(
                          variant === "secondary"
                            ? styles.itemsRow
                            : styles.column,
                        )}
                      >
                        {cardConfig ? (
                          <Card
                            {...item}
                            variant={getVariantByIndex(cardConfig, index)}
                            mobileUpVariant={getVariantByIndex(
                              cardConfig.mobileUp,
                              index,
                            )}
                            placeholders={placeholders}
                          />
                        ) : (
                          <LatestArticle
                            {...item}
                            mode={
                              configHome.contentBlockG?.landscapeIndexes?.includes(
                                index,
                              )
                                ? "landscape"
                                : configHome.contentBlockG?.originalIndexes?.includes(
                                      index,
                                    )
                                  ? "original"
                                  : "portrait"
                            }
                            mobileMode={configHome.contentBlockG?.mobileMode}
                            placeholders={placeholders}
                            landscapeImageWidth={
                              configHome.contentBlockG?.landscapeImageWidth ||
                              315
                            }
                            portraitImageWidth={
                              configHome.contentBlockG?.portraitImageWidth ||
                              315
                            }
                            displayPublishDate={configTeaser.publishDate}
                            mobileUpAspectRatio={
                              configHome.contentBlockG?.originalIndexes?.includes(
                                index,
                              )
                                ? configHome.contentBlockG
                                    .originalMobileUpAspectRatio
                                : undefined
                            }
                            aspectRatio={
                              configHome.contentBlockG?.originalIndexes?.includes(
                                index,
                              )
                                ? configHome.contentBlockG.originalAspectRatio
                                : undefined
                            }
                          />
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </SectionContext.Provider>
          </ContentBlock>
        </div>
      </div>
    )
  );
}
